import { supabase } from './supabase.js';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';
const API_PREFIX = '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    const bodyText = typeof body === 'string' ? body : JSON.stringify(body);
    super(`API ${status}: ${bodyText}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Reads `error.code` from an ApiError JSON body (e.g. 'plan_required', 'seat_limit_reached'). */
export function apiErrorCode(err: unknown): string | undefined {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const body = err.body as { error?: { code?: unknown } };
    if (typeof body.error?.code === 'string') return body.error.code;
  }
  return undefined;
}

/** The server's own `error.message`, falling back to the error's text and then to `fallback`. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const message = (err.body as { error?: { message?: unknown } }).error?.message;
    if (typeof message === 'string' && message) return message;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

export function normalizeApiPath(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (normalizedPath === API_PREFIX || normalizedPath.startsWith(`${API_PREFIX}/`)) {
    return normalizedPath;
  }
  return `${API_PREFIX}${normalizedPath}`;
}

export function absoluteApiUrl(path: string): string {
  return `${API_URL}${normalizeApiPath(path)}`;
}

async function authHeader(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

async function readApiErrorBody(res: Response): Promise<unknown> {
  const text = await res.text().catch(() => '');
  if (!text) return '';

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const auth = await authHeader();
  const hasFormDataBody = typeof FormData !== 'undefined' && init.body instanceof FormData;
  const headers = new Headers();
  if (!hasFormDataBody) headers.set('Content-Type', 'application/json');
  for (const [key, value] of Object.entries(auth)) headers.set(key, value);
  new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  const res = await fetch(`${API_URL}${normalizeApiPath(path)}`, {
    ...init,
    headers,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await readApiErrorBody(res));
  }
  // No body to parse -- res.json() throws SyntaxError on an empty 204 response.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiFetchBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const auth = await authHeader();
  const headers = new Headers();
  for (const [key, value] of Object.entries(auth)) headers.set(key, value);
  new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  const res = await fetch(`${API_URL}${normalizeApiPath(path)}`, {
    ...init,
    headers,
  });
  if (!res.ok) {
    throw new ApiError(res.status, await readApiErrorBody(res));
  }
  return res.blob();
}
